/**
 * PortfolioModule - viewer-scoped portfolio command panel.
 *
 * WO3 user-data-isolation: drops useDefaultAccount() (which could surface an
 * Adversyn internal sim account as "Your Portfolio") and binds to the new
 * useViewerPortfolio() hook which calls GET /api/v1/user/portfolio. The
 * backend filters internal RAM / Agro / sim-* / 39156672 account IDs before
 * the payload is assembled - no internal account ever reaches this view.
 *
 * Three render states:
 *   source === 'none'             -> PortfolioEmptyState (no numbers shown)
 *   source === 'connected_broker' -> full portfolio with viewer's own data
 *   loading                       -> light loading placeholder
 *
 * Adversyn's house simulation accounts (sim-brain-scalper, sim-owl-momentum,
 * sim-compass-swing, sim-forex-oanda) live in Simulation Lab and are
 * read-only. Nothing from this module falls back into one of those IDs.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, Info, Link2, Beaker } from 'lucide-react';
import { PortfolioHeader, PortfolioMetricsBar } from './PortfolioHeader';
import { PerformanceChart } from './PerformanceChart';
import { PositionsTable } from './PositionsTable';
import { AllocationChart } from './AllocationChart';
import { OrdersPanel } from './OrdersPanels';
import { AnalyticsPanel, AIInsightsPanel } from './AnalyticsPanels';
import { TradeJournal } from './TradeJournal';
import { cn, logInteraction } from '../lib/utils';
// 2026-06-06 h-source-of-truth-purge — mock arrays removed from this
// module. Performance / AI Insights / Journal panels now render their
// own "Not available from current sync" empty states sourced from
// /api/v1/user/portfolio. The PortfolioService mockX exports remain
// only for backward-compat with any external consumer.
import type {
  Account as PortfolioAccount,
  Position as PortfolioPosition,
  PortfolioHistory,
} from '../types/portfolio';
import useViewerPortfolio, { VIEWER_PORTFOLIO_EMPTY } from '../hooks/useViewerPortfolio';
// 2026-06-06 i-visible-defect-purge — Performance panel pulls from
// /api/v1/user/portfolio/performance-history (J-batch).
import PerformancePanelLive from './PerformancePanelLive';

/**
 * EMPTY-STATE COPY - sourced verbatim from the WO2 backend constant
 * EMPTY_STATE_COPY in api_gateway/portfolio/user_portfolio.py. Kept here as
 * a fallback when the API call fails or the user is unauthenticated.
 *
 * Banned-token scrub note: this string was passed through the WO3
 * defensive scan list (call-to-action verbs) and contains none of them.
 */
const PORTFOLIO_EMPTY_FALLBACK =
  'No paper trading account connected yet. ' +
  'Adversyn does not yet support live broker linking - ' +
  'your activity will live in Simulation Lab.';

function PortfolioEmptyState({
  message,
  onConnectClicked,
}: {
  message: string;
  onConnectClicked: () => void;
}) {
  return (
    <div
      data-testid="portfolio-empty-state"
      className="bg-[var(--app-bg)] pb-20"
    >
      <div className="app-container pt-12 pb-8">
        <div>
          <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">
            Your Portfolio
          </h1>
          <p className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest mt-1 italic">
            Viewer-scoped paper trading account.
          </p>
        </div>
      </div>
      <div className="app-container">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-10 shadow-sm max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-slate-500/10 flex items-center justify-center text-slate-500">
              <Link2 size={20} />
            </div>
            <h2 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">
              No paper account connected
            </h2>
          </div>
          <p
            data-testid="portfolio-empty-copy"
            className="text-sm text-[var(--muted-text)] font-medium leading-relaxed mb-6"
          >
            {message}
          </p>
          {/*
            Primary CTA = provider-neutral "Connect trading account".
            Simulation Lab demoted to a secondary informational link so the
            demo user can still discover the house sim accounts, but it is
            NOT presented as the recommended portfolio action.
          */}
          <div className="flex flex-wrap gap-3">
            <a
              data-testid="portfolio-connect-cta"
              href="/account?tab=brokerage&connect=alpaca_paper"
              onClick={() => {
                // WO7 portfolio-connect-cta autoconnect token (2026-06-05).
                try { sessionStorage.setItem('adversyn_account_autoconnect', 'alpaca_paper'); } catch {}
                logInteraction('Portfolio', 'Open Alpaca Paper Self-Connect from Empty State', {});
                if (typeof onConnectClicked === 'function') onConnectClicked();
              }}
              title="Open the Connected Brokerage page to start a request"
              className="px-5 py-3 rounded-xl bg-emerald-600 text-white text-xs font-black uppercase italic tracking-widest hover:bg-emerald-700 transition-all inline-flex items-center gap-2"
            >
              Connect trading account
            </a>
            <a
              href="/simulation-lab"
              data-testid="portfolio-open-simulation-lab"
              className="px-5 py-3 rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] text-xs font-black uppercase italic tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)] transition-all inline-flex items-center gap-2"
              onClick={() =>
                logInteraction('Portfolio', 'Open Simulation Lab from Empty State', {})
              }
            >
              <Beaker size={14} />
              Open Simulation Lab (demo, read-only)
            </a>
          </div>
          <div className="mt-6 pt-6 border-t border-[var(--card-border)] text-[11px] font-bold text-[var(--muted-text)] uppercase tracking-widest opacity-80">
            Adversyn's house simulation accounts live in Simulation Lab and are read-only.
            They are never shown under Your Portfolio.
          </div>
        </div>
      </div>
    </div>
  );
}

function viewerToPortfolioAccount(
  v: ReturnType<typeof useViewerPortfolio>['data']
): PortfolioAccount {
  // 2026-06-06 alpaca-paper-real-account-sync — prefer the synced Alpaca
  // account snapshot when present. PortfolioMetricsBar will render em-dashes
  // anyway when sync_status !== 'synced', so the fallback values here are
  // only ever shown when the data IS live.
  const acct = (v as any).account || null;
  const accountNumberMasked = acct?.account_number_masked
    || (v.broker_connection_summary?.display_name || v.user_id || 'viewer');
  return {
    id: v.user_id || 'viewer',
    account_number: accountNumberMasked,
    status: v.paper_account_connected ? 'active' : 'no_broker',
    currency: acct?.currency || v.base_currency || 'USD',
    buying_power: acct?.buying_power ?? String(v.buying_power ?? ''),
    regt_buying_power: acct?.buying_power ?? String(v.buying_power ?? ''),
    daytrading_buying_power: acct?.daytrade_buying_power ?? String(v.buying_power ?? ''),
    cash: acct?.cash ?? String(v.cash ?? ''),
    portfolio_value: acct?.portfolio_value ?? String(v.portfolio_value ?? ''),
    equity: acct?.equity ?? String(v.equity ?? ''),
    last_equity: acct?.last_equity ?? acct?.equity ?? String(v.equity ?? ''),
    long_market_value: acct?.long_market_value ?? '0',
    short_market_value: acct?.short_market_value ?? '0',
    initial_margin: acct?.initial_margin ?? '0',
    maintenance_margin: acct?.maintenance_margin ?? '0',
    last_maintenance_margin: acct?.maintenance_margin ?? '0',
    sma: '0',
    daytrade_count: 0,
    balance_as_of: v.last_sync_at || new Date().toISOString(),
  };
}

export default function PortfolioModule({
  onOpenNotifications,
}: {
  onOpenNotifications?: () => void;
}) {
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const { data: viewer, loading, error, refetch } = useViewerPortfolio();
  const [refreshing, setRefreshing] = useState(false);

  // Server-authoritative source. Defaults to 'none' for safety.
  const source = viewer.source || 'none';
  // WO12 portfolio connected source (2026-06-06) — both legacy
  // 'connected_broker' and new 'alpaca_paper_self_connected' values
  // mean the user has a live broker row. Either should activate the
  // populated portfolio view.
  const _isViewerConnected = source === 'connected_broker' || source === 'alpaca_paper_self_connected';
  const account = useMemo<PortfolioAccount | null>(
    () => (_isViewerConnected ? viewerToPortfolioAccount(viewer) : null),
    [_isViewerConnected, viewer]
  );

  // 2026-06-06 alpaca-paper-real-account-sync — sync_status drives every
  // numeric display. When connected but pending/failed we render em-dashes
  // (never fake values) and surface a small banner.
  const syncStatus = (viewer as any).sync_status as
    | 'not_connected'
    | 'sync_pending'
    | 'synced'
    | 'sync_failed'
    | undefined;
  const lastSyncAt = (viewer as any).last_success_at || (viewer as any).last_sync_at || null;

  // 2026-06-06 — real positions / open orders from backend snapshot.
  const positions: PortfolioPosition[] = useMemo(() => {
    const raw: any[] = Array.isArray((viewer as any).positions) ? (viewer as any).positions : [];
    return raw.map((p) => ({
      symbol: p.symbol ?? '',
      qty: Number(p.qty ?? 0),
      side: (p.side as any) ?? 'long',
      market_value: String(p.market_value ?? '0'),
      avg_entry_price: String(p.avg_entry_price ?? '0'),
      unrealized_pl: String(p.unrealized_pl ?? '0'),
      unrealized_plpc: String(p.unrealized_plpc ?? '0'),
      current_price: String(p.current_price ?? '0'),
      lastday_price: String(p.lastday_price ?? '0'),
      change_today: String(p.change_today ?? '0'),
    })) as any;
  }, [viewer]);
  const openOrders = useMemo(() => {
    const raw: any[] = Array.isArray((viewer as any).open_orders) ? (viewer as any).open_orders : [];
    return raw as any[];
  }, [viewer]);
  const recentOrders: any[] = [];

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const token = typeof localStorage !== 'undefined'
        ? localStorage.getItem('adversyn_auth_token') : null;
      if (!token) {
        setNotification({ message: 'Refresh failed: not authenticated.', type: 'error' });
        return;
      }
      const cfg = await import('../lib/apiConfig');
      const evts = await import('../lib/refreshEvents');
      let syncOk = false;
      let syncMsg = '';
      try {
        const r = await fetch(
          cfg.apiV1Base() + '/user/broker-connections/alpaca-paper/sync',
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'omit',
          },
        );
        syncOk = r.ok;
        if (!r.ok) syncMsg = `HTTP ${r.status}`;
      } catch (e: any) {
        syncMsg = String(e?.message || e);
      }
      try { if (typeof refetch === 'function') refetch(); } catch { /* ignore */ }
      // 2026-06-07 sitewide-refresh — tell PerformancePanelLive to
      // refetch /user/portfolio/performance-history too.
      try { evts.emitRefresh('portfolio', { syncOk }); } catch { /* ignore */ }
      if (syncOk) {
        setNotification({ message: 'Updated just now', type: 'success' });
      } else {
        setNotification({ message: `Refresh failed: ${syncMsg || 'sync unavailable'}.`, type: 'error' });
      }
    } finally {
      setRefreshing(false);
    }
  };
  const history: PortfolioHistory = {
    timestamp: [],
    equity: [],
    profit_loss: [],
    profit_loss_pct: [],
    base_value: 0,
    timeframe: '1M',
  };
  // 2026-06-06 g3-no-fake-data-outside-sim-lab — Performance / AI
  // Insights / Journal panels MUST NOT render mockPerformanceMetrics,
  // mockAIInsights, or mockJournalEntries. Backend doesn't yet emit
  // these for the user's Alpaca paper account, so they stay null/[]
  // and the panels render an "available after first synced trade"
  // empty state.
  const metrics = null;
  const insights = null;
  const journalEntries: any[] = [];

  if (loading) {
    return (
      <div
        data-testid="portfolio-loading"
        className="flex items-center justify-center h-64 text-[var(--muted-text)] text-sm font-bold uppercase tracking-widest"
      >
        Loading portfolio...
      </div>
    );
  }

  // WO12 — empty state only when truly no source.
  if (source === 'none' || !account) {
    const msg = viewer.empty_state_copy || PORTFOLIO_EMPTY_FALLBACK;
    return (
      <PortfolioEmptyState
        message={msg}
        onConnectClicked={() =>
          logInteraction('Portfolio', 'Connect CTA (disabled)', {})
        }
      />
    );
  }

  return (
    <div className="bg-[var(--app-bg)] pb-20 overflow-visible">
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 20, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className={cn(
              'fixed top-4 left-1/2 z-50 px-6 py-3 rounded-2xl shadow-2xl border flex items-center gap-3 min-w-[300px]',
              notification.type === 'success'
                ? 'bg-emerald-600 border-emerald-500 text-white'
                : 'bg-rose-600 border-rose-500 text-white'
            )}
          >
            {notification.type === 'success' ? (
              <CheckCircle2 size={18} />
            ) : (
              <Info size={18} />
            )}
            <span className="text-xs font-black uppercase italic tracking-widest">
              {notification.message}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <PortfolioHeader
        account={account}
        source={source}
        syncStatus={syncStatus}
        lastSyncAt={lastSyncAt}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        onOpenNotifications={onOpenNotifications}
      />

      {syncStatus && syncStatus !== 'synced' && syncStatus !== 'not_connected' && (
        <div
          data-testid="portfolio-sync-banner"
          data-sync-status={syncStatus}
          className="app-container mt-4"
        >
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs font-bold uppercase tracking-widest text-amber-700">
            {syncStatus === 'sync_failed'
              ? 'Broker connected. Balance sync unavailable. Use Refresh Broker Data to retry.'
              : 'Broker connected. Waiting for Alpaca balance sync.'}
          </div>
        </div>
      )}

      <div className="app-container pt-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1
              data-testid="portfolio-page-title"
              className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display"
            >
              Your Portfolio
            </h1>
            <p className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest mt-1 italic">
              Viewer-scoped paper trading activity.
            </p>
          </div>
        </div>
      </div>

      <PortfolioMetricsBar account={account} source={source} syncStatus={syncStatus} />

      <div className="app-container py-8 space-y-8">
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start"
          style={{
            gridTemplateAreas:
              '"performance performance aiInsights" "positions positions sectorAllocation" "openOrders recentOrders statsSummary" "tradeJournal tradeJournal tradeJournal"',
          }}
        >
          <div className="lg:col-span-2" style={{ gridArea: 'performance' }}>
            <PerformancePanelLive />
          </div>

          <div style={{ gridArea: 'aiInsights' }}>
            {insights ? <AIInsightsPanel insights={insights} /> : <div data-testid="portfolio-ai-insights-unavailable" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] text-center">AI Insights — Not available from current sync</div>}
          </div>

          <div className="lg:col-span-2" style={{ gridArea: 'positions' }}>
            <PositionsTable
              positions={positions}
              onMoreOptions={(pos) => {
                logInteraction('Portfolio', 'Position Options', { symbol: pos.symbol });
                setNotification({
                  message: 'Opening advanced options for ' + pos.symbol + '...',
                  type: 'success',
                });
              }}
            />
          </div>

          <div style={{ gridArea: 'sectorAllocation' }}>
            <AllocationChart positions={positions} />
          </div>

          <div style={{ gridArea: 'openOrders' }}>
            <OrdersPanel
              orders={openOrders}
              title="Open Orders"
              type="open"
              onEdit={(order) => {
                logInteraction('Portfolio', 'Edit Order', { symbol: order.symbol });
                setNotification({
                  message: 'Modifying ' + order.symbol + ' order...',
                  type: 'success',
                });
              }}
              onDelete={(order) => {
                logInteraction('Portfolio', 'Cancel Order', { symbol: order.symbol });
                setNotification({
                  message: 'Order for ' + order.symbol + ' has been canceled.',
                  type: 'success',
                });
              }}
            />
          </div>

          <div style={{ gridArea: 'recentOrders' }}>
            <OrdersPanel orders={recentOrders} title="Recent Orders" type="recent" />
          </div>

          <div style={{ gridArea: 'statsSummary' }}>
            {metrics ? <AnalyticsPanel metrics={metrics} /> : <div data-testid="portfolio-analytics-unavailable" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] text-center">Performance Analytics — Not available from current sync</div>}
          </div>

          <div className="lg:col-span-3" style={{ gridArea: 'tradeJournal' }}>
            {(!journalEntries || journalEntries.length === 0) && (
              <div data-testid="portfolio-journal-unavailable" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 mb-4 text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] text-center">Trade Journal — Not available from current sync</div>
            )}
            <TradeJournal
              entries={journalEntries}
              onSave={(entry) => {
                logInteraction('Portfolio', 'Save Journal Entry', { symbol: entry.symbol });
                setNotification({
                  message: 'Journal entry for ' + entry.symbol + ' saved successfully.',
                  type: 'success',
                });
              }}
            />
          </div>
        </div>
      </div>

      {error ? (
        <div
          data-testid="portfolio-error"
          className="app-container text-xs font-bold text-rose-500 uppercase tracking-widest"
        >
          Portfolio data partially unavailable.
        </div>
      ) : null}
    </div>
  );
}

export { VIEWER_PORTFOLIO_EMPTY };
